import { ArrowLeft, Droplets, Receipt, Save, Target, Wallet } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { SectionCard } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { selectUser, useAuthStore } from '@/features/auth/auth.store';
import { toIsoDate, toNumber } from '@/lib/utils';

import { ControlArea, ControlSelect } from '../components/meta-campos';
import { MetasNav } from '../components/metas-nav';
import { useMeta, useMetaLados, useMetaProductos, useMetaUsuarios } from '../hooks/use-metas';
import { useActualizarMeta, useCrearMeta } from '../hooks/use-metas-mutations';
import {
  ESTADO_LABELS,
  ESTADOS_ORDENADOS,
  PRIORIDAD_LABELS,
  PRIORIDADES_ORDENADAS,
  TIPO_LABELS,
  TIPOS_ORDENADOS,
  UNIDADES_POR_TIPO,
} from '../lib/metas-format';
import type { Goal, GoalPayload, GoalPriority, GoalStatus, GoalType } from '../metas.types';

/** Estado del formulario: los números se guardan como texto mientras se editan. */
interface FormState {
  goal_type: GoalType;
  goal_name: string;
  target_value: string;
  unit_measurement: string;
  start_date: string;
  end_date: string;
  priority: GoalPriority;
  /** `'all'` o el id del producto. */
  fuel_grade_id: string;
  /** `'all_sides'` o el id del lado. */
  pump_id: string;
  include_credits: boolean;
  include_internal_consumption: boolean;
  include_calibrations: boolean;
  /** `''` (general) o el id del responsable. */
  user_id: string;
  notes: string;
  reward_description: string;
  status: GoalStatus;
}

const UNIDADES_SUGERIDAS = new Set(Object.values(UNIDADES_POR_TIPO));

/** Valores iniciales: los de la meta o los de una meta nueva del mes en curso. */
function estadoInicial(goal: Goal | null): FormState {
  if (!goal) {
    const hoy = new Date();

    return {
      goal_type: 'volume',
      goal_name: '',
      target_value: '',
      unit_measurement: UNIDADES_POR_TIPO.volume ?? 'Galones',
      start_date: toIsoDate(new Date(hoy.getFullYear(), hoy.getMonth(), 1)),
      end_date: toIsoDate(new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0)),
      priority: 'medium',
      fuel_grade_id: 'all',
      pump_id: 'all_sides',
      include_credits: true,
      include_internal_consumption: true,
      include_calibrations: true,
      user_id: '',
      notes: '',
      reward_description: '',
      status: 'active',
    };
  }

  return {
    goal_type: goal.goal_type,
    goal_name: goal.goal_name,
    target_value: String(toNumber(goal.target_value)),
    unit_measurement: goal.unit_measurement ?? '',
    start_date: toIsoDate(new Date(goal.start_date)),
    end_date: toIsoDate(new Date(goal.end_date)),
    priority: goal.priority,
    fuel_grade_id: goal.fuel_grade_id === null ? 'all' : String(goal.fuel_grade_id),
    pump_id: goal.pump_id === null ? 'all_sides' : String(goal.pump_id),
    include_credits: Boolean(goal.include_credits),
    include_internal_consumption: Boolean(goal.include_internal_consumption),
    include_calibrations: Boolean(goal.include_calibrations),
    user_id: goal.user_id === null ? '' : String(goal.user_id),
    notes: goal.notes ?? '',
    reward_description: goal.reward_description ?? '',
    status: goal.status,
  };
}

/** Validación espejo de `validateGoalPayload` en el backend. */
function validar(valores: FormState): Record<string, string> {
  const errores: Record<string, string> = {};

  if (!valores.goal_name.trim()) errores.goal_name = 'El nombre de la meta es obligatorio.';
  if (!(toNumber(valores.target_value) > 0)) {
    errores.target_value = 'El valor objetivo debe ser mayor a 0.';
  }
  if (!valores.unit_measurement.trim()) {
    errores.unit_measurement = 'Indica la unidad de medida (ej. Galones, Soles).';
  }
  if (!valores.start_date) errores.start_date = 'Selecciona la fecha de inicio.';
  if (!valores.end_date) {
    errores.end_date = 'Selecciona la fecha de fin.';
  } else if (valores.start_date && valores.end_date <= valores.start_date) {
    errores.end_date = 'La fecha de fin debe ser posterior a la fecha de inicio.';
  }

  return errores;
}

/**
 * Formulario de metas (`/metas/nueva` y `/metas/:id/editar`).
 *
 * El backend exige `created_by` al crear, así que se envía el usuario de la
 * sesión; sin sesión no se permite guardar.
 */
export function MetaFormPage() {
  const { id: idParam } = useParams();
  const id = idParam ? Number(idParam) : null;
  const esEdicion = id !== null && Number.isFinite(id);

  const meta = useMeta(esEdicion ? id : null);

  useDocumentTitle(esEdicion ? 'Metas · Editar' : 'Metas · Nueva');

  if (esEdicion && meta.isPending) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-20 rounded-card" />
        <Skeleton className="h-96 rounded-card" />
      </div>
    );
  }

  if (esEdicion && (meta.isError || !meta.data)) {
    return (
      <Alert variant="error" title="No se pudo cargar la meta">
        <p>La meta no existe o no tienes acceso a ella.</p>
        <Link to="/metas/listado" className="mt-2 inline-flex text-sm font-semibold text-brand-700">
          Volver al listado
        </Link>
      </Alert>
    );
  }

  return <MetaFormulario goal={meta.data ?? null} />;
}

/** Identificadores de las plantillas de autorrellenado (espejo del panel anterior). */
type PlantillaId = 'monthly_volume' | 'weekly_sales' | 'daily_transactions';

function MetaFormulario({ goal }: { goal: Goal | null }) {
  const esEdicion = goal !== null;
  const navigate = useNavigate();
  const usuario = useAuthStore(selectUser);

  const productos = useMetaProductos();
  const lados = useMetaLados();
  const usuarios = useMetaUsuarios();

  const crear = useCrearMeta();
  const actualizar = useActualizarMeta();

  const [valores, setValores] = useState<FormState>(() => estadoInicial(goal));
  const [errores, setErrores] = useState<Record<string, string>>({});
  const [aviso, setAviso] = useState<string | null>(null);

  const guardando = crear.isPending || actualizar.isPending;

  const cambiar = <C extends keyof FormState>(campo: C, valor: FormState[C]) => {
    setValores((prev) => ({ ...prev, [campo]: valor }));
  };

  /** Al cambiar el tipo se sugiere la unidad, salvo que ya se haya personalizado. */
  const cambiarTipo = (tipo: GoalType) => {
    const sugerida = UNIDADES_POR_TIPO[tipo];

    setValores((prev) => ({
      ...prev,
      goal_type: tipo,
      unit_measurement:
        sugerida && (prev.unit_measurement === '' || UNIDADES_SUGERIDAS.has(prev.unit_measurement))
          ? sugerida
          : prev.unit_measurement,
    }));
  };

  /** Aplica una plantilla de autorrellenado (espejo de las del panel anterior). */
  const aplicarPlantilla = (plantilla: PlantillaId) => {
    const hoy = new Date();
    const finMes = new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0);
    const finSemana = new Date(hoy);
    finSemana.setDate(hoy.getDate() + 7);

    const porPlantilla: Record<PlantillaId, Partial<FormState>> = {
      monthly_volume: {
        goal_name: `Meta Mensual de Volumen - ${hoy.getMonth() + 1}/${hoy.getFullYear()}`,
        goal_type: 'volume',
        target_value: '50000',
        unit_measurement: 'Galones',
        start_date: toIsoDate(new Date(hoy.getFullYear(), hoy.getMonth(), 1)),
        end_date: toIsoDate(finMes),
        priority: 'high',
      },
      weekly_sales: {
        goal_name: 'Meta Semanal de Ventas',
        goal_type: 'amount',
        target_value: '100000',
        unit_measurement: 'Soles',
        start_date: toIsoDate(hoy),
        end_date: toIsoDate(finSemana),
        priority: 'medium',
      },
      daily_transactions: {
        goal_name: 'Meta Diaria de Transacciones',
        goal_type: 'transactions',
        target_value: '200',
        unit_measurement: 'Transacciones',
        start_date: toIsoDate(hoy),
        end_date: toIsoDate(hoy),
        priority: 'medium',
      },
    };

    setValores((prev) => ({ ...prev, ...porPlantilla[plantilla] }));
    setErrores({});
    setAviso(null);
  };

  const enviar = () => {
    const encontrados = validar(valores);

    setErrores(encontrados);
    setAviso(null);

    if (Object.keys(encontrados).length > 0) {
      setAviso('Revisa los campos marcados antes de guardar.');
      return;
    }

    if (!esEdicion && !usuario?.id) {
      setAviso('No se pudo identificar al usuario de la sesión para registrar la creación.');
      return;
    }

    const producto = (productos.data ?? []).find(
      (opcion) => String(opcion.id) === valores.fuel_grade_id,
    );

    const payload: GoalPayload = {
      goal_type: valores.goal_type,
      goal_name: valores.goal_name.trim(),
      target_value: toNumber(valores.target_value),
      unit_measurement: valores.unit_measurement.trim(),
      start_date: valores.start_date,
      end_date: valores.end_date,
      priority: valores.priority,
      fuel_grade_id: valores.fuel_grade_id === 'all' ? 'all' : Number(valores.fuel_grade_id),
      fuel_grade_name: producto?.name ?? (valores.fuel_grade_id === 'all' ? 'Todos los productos' : null),
      pump_id: valores.pump_id === 'all_sides' ? 'all_sides' : Number(valores.pump_id),
      include_credits: valores.include_credits,
      include_internal_consumption: valores.include_internal_consumption,
      include_calibrations: valores.include_calibrations,
      user_id: valores.user_id ? Number(valores.user_id) : null,
      notes: valores.notes.trim() || null,
      reward_description: valores.reward_description.trim() || null,
    };

    const alGuardar = (metaGuardada: Goal) => {
      navigate(`/metas/${metaGuardada.id}`, { replace: true });
    };

    if (esEdicion && goal) {
      actualizar.mutate(
        { id: goal.id, payload: { ...payload, status: valores.status } },
        {
          onSuccess: (respuesta) => alGuardar(respuesta.goal),
          onError: (error) => setAviso(error.message || 'No se pudo actualizar la meta.'),
        },
      );
      return;
    }

    crear.mutate(
      { ...payload, created_by: usuario?.id ?? null },
      {
        onSuccess: (respuesta) => alGuardar(respuesta.goal),
        onError: (error) => setAviso(error.message || 'No se pudo crear la meta.'),
      },
    );
  };

  return (
    <div className="space-y-6">
      <header className="space-y-4">
        <Link
          to={esEdicion ? `/metas/${goal.id}` : '/metas/listado'}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-ink-500 transition-colors hover:text-brand-700"
        >
          <ArrowLeft className="size-3.5" aria-hidden="true" />
          {esEdicion ? 'Volver al detalle' : 'Volver al listado'}
        </Link>

        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">Metas</p>
            <h1 className="mt-1 flex items-center gap-2 text-2xl font-bold tracking-tight text-ink-900">
              <Target className="size-6 shrink-0 text-brand-500" aria-hidden="true" />
              {esEdicion ? 'Editar meta' : 'Nueva meta'}
            </h1>
            <p className="mt-1 text-sm text-ink-500">
              {esEdicion
                ? `Actualiza los datos de “${goal.goal_name}”.`
                : 'Define el objetivo, el período y las condiciones de cálculo del avance.'}
            </p>
          </div>

          <MetasNav active="listado" sinNueva className="justify-end" />
        </div>
      </header>

      {aviso ? (
        <Alert variant="error" title="No se pudo guardar">
          {aviso}
        </Alert>
      ) : null}

      {!esEdicion ? (
        <SectionCard
          title="Plantillas"
          description="Autorrellena el formulario con una meta típica y ajústala después."
        >
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => aplicarPlantilla('monthly_volume')}>
              <Droplets className="size-4" aria-hidden="true" />
              Meta mensual de volumen
            </Button>
            <Button variant="outline" size="sm" onClick={() => aplicarPlantilla('weekly_sales')}>
              <Wallet className="size-4" aria-hidden="true" />
              Meta semanal de ventas
            </Button>
            <Button variant="outline" size="sm" onClick={() => aplicarPlantilla('daily_transactions')}>
              <Receipt className="size-4" aria-hidden="true" />
              Meta diaria de transacciones
            </Button>
          </div>
        </SectionCard>
      ) : null}

      <SectionCard title="Datos generales" description="Nombre, tipo y objetivo de la meta">
        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            containerClassName="sm:col-span-2"
            label="Nombre de la meta"
            value={valores.goal_name}
            error={errores.goal_name}
            placeholder="Ej. Meta mensual de volumen"
            onChange={(event) => cambiar('goal_name', event.target.value)}
          />

          <ControlSelect
            label="Tipo de meta"
            value={valores.goal_type}
            onChange={(event) => cambiarTipo(event.target.value as GoalType)}
          >
            {TIPOS_ORDENADOS.map((tipo) => (
              <option key={tipo} value={tipo}>
                {TIPO_LABELS[tipo]}
              </option>
            ))}
          </ControlSelect>

          <ControlSelect
            label="Prioridad"
            value={valores.priority}
            onChange={(event) => cambiar('priority', event.target.value as GoalPriority)}
          >
            {PRIORIDADES_ORDENADAS.map((prioridad) => (
              <option key={prioridad} value={prioridad}>
                {PRIORIDAD_LABELS[prioridad]}
              </option>
            ))}
          </ControlSelect>

          <Input
            label="Valor objetivo"
            type="number"
            min={0}
            step="0.01"
            inputMode="decimal"
            value={valores.target_value}
            error={errores.target_value}
            placeholder="Ej. 50000"
            onChange={(event) => cambiar('target_value', event.target.value)}
          />

          <Input
            label="Unidad de medida"
            value={valores.unit_measurement}
            error={errores.unit_measurement}
            hint="Galones, Soles, Transacciones…"
            onChange={(event) => cambiar('unit_measurement', event.target.value)}
          />

          {esEdicion ? (
            <ControlSelect
              label="Estado"
              value={valores.status}
              onChange={(event) => cambiar('status', event.target.value as GoalStatus)}
              hint="Se guarda junto con el resto de los cambios."
            >
              {ESTADOS_ORDENADOS.map((estado) => (
                <option key={estado} value={estado}>
                  {ESTADO_LABELS[estado]}
                </option>
              ))}
            </ControlSelect>
          ) : null}
        </div>
      </SectionCard>

      <SectionCard title="Período" description="Rango de fechas que cubre la meta">
        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Fecha de inicio"
            type="date"
            value={valores.start_date}
            error={errores.start_date}
            onChange={(event) => cambiar('start_date', event.target.value)}
          />

          <Input
            label="Fecha de fin"
            type="date"
            value={valores.end_date}
            min={valores.start_date || undefined}
            error={errores.end_date}
            hint="Debe ser posterior a la fecha de inicio."
            onChange={(event) => cambiar('end_date', event.target.value)}
          />
        </div>
      </SectionCard>

      <SectionCard title="Alcance" description="Producto, lado y responsable del cumplimiento">
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          <ControlSelect
            label="Producto"
            value={valores.fuel_grade_id}
            onChange={(event) => cambiar('fuel_grade_id', event.target.value)}
          >
            <option value="all">Todos los productos</option>
            {(productos.data ?? []).map((producto) => (
              <option key={producto.id} value={String(producto.id)}>
                {producto.name ?? producto.text ?? `Producto ${producto.id}`}
              </option>
            ))}
          </ControlSelect>

          <ControlSelect
            label="Lado"
            value={valores.pump_id}
            onChange={(event) => cambiar('pump_id', event.target.value)}
          >
            <option value="all_sides">Todos los lados</option>
            {(lados.data ?? []).map((lado) => (
              <option key={lado.id} value={String(lado.id)}>
                {lado.name ?? lado.text ?? `Lado ${lado.id}`}
              </option>
            ))}
          </ControlSelect>

          <ControlSelect
            label="Responsable"
            value={valores.user_id}
            onChange={(event) => cambiar('user_id', event.target.value)}
          >
            <option value="">General (sin responsable)</option>
            {(usuarios.data ?? []).map((opcion) => (
              <option key={opcion.id} value={String(opcion.id)}>
                {opcion.text ?? opcion.name ?? `Usuario ${opcion.id}`}
              </option>
            ))}
          </ControlSelect>
        </div>
      </SectionCard>

      <SectionCard title="Cálculo y notas" description="Qué se incluye al medir el avance">
        <div className="space-y-3">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-400">
            Inclusiones del cálculo
          </p>
          <p className="text-xs text-ink-400">
            Aplican al acumulado que el sistema recalcula con la operación real.
          </p>

          <div className="space-y-3 pt-1">
            <Checkbox
              label="Incluir créditos"
              hint="Suma las ventas a crédito del período."
              checked={valores.include_credits}
              onChange={(event) => cambiar('include_credits', event.target.checked)}
            />
            <Checkbox
              label="Incluir consumo interno"
              hint="Suma los despachos de consumo propio de la estación."
              checked={valores.include_internal_consumption}
              onChange={(event) => cambiar('include_internal_consumption', event.target.checked)}
            />
            <Checkbox
              label="Incluir calibraciones"
              hint="Suma los despachos usados en calibración de mangueras."
              checked={valores.include_calibrations}
              onChange={(event) => cambiar('include_calibrations', event.target.checked)}
            />
          </div>
        </div>

        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <ControlArea
            label="Notas"
            value={valores.notes}
            placeholder="Observaciones sobre la meta…"
            onChange={(event) => cambiar('notes', event.target.value)}
          />
          <ControlArea
            label="Recompensa"
            value={valores.reward_description}
            placeholder="Incentivo al alcanzar la meta…"
            onChange={(event) => cambiar('reward_description', event.target.value)}
          />
        </div>
      </SectionCard>

      <div className="flex flex-wrap items-center justify-end gap-3">
        <Link
          to={esEdicion ? `/metas/${goal.id}` : '/metas/listado'}
          className="inline-flex h-11 items-center rounded-control border border-ink-200 bg-white px-5 text-sm font-semibold text-ink-800 transition-colors hover:border-brand-300 hover:text-brand-700"
        >
          Cancelar
        </Link>

        <Button
          leftIcon={<Save className="size-4" aria-hidden="true" />}
          loading={guardando}
          onClick={enviar}
        >
          {esEdicion ? 'Guardar cambios' : 'Crear meta'}
        </Button>
      </div>
    </div>
  );
}
